// src/components/ProductDemoModal.tsx
import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  videoUrl?: string | null;
  source?: 'youtube' | 'upload' | null;
}

export default function ProductDemoModal({
  isOpen,
  onClose,
  videoUrl,
  source,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Stop MP4 playback on close
  useEffect(() => {
    if (!isOpen && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="animate-fadeIn fixed inset-0 z-[999] flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
      {/* === CLOSE BUTTON === */}
      <button
        className="absolute right-6 top-6 z-[1000] rounded-full p-2 text-[#FFBD59] transition hover:bg-[#FFBD59]/20"
        onClick={onClose}
      >
        <X size={30} />
      </button>

      {/* === MODAL CARD === */}
      <div
        className="
          relative w-full max-w-3xl overflow-hidden rounded-xl 
          border border-[#FFBD59] bg-[#111]/90 
          shadow-[0_0_25px_rgba(255,189,89,0.25)] backdrop-blur-md
      "
      >
        {/* HEADER */}
        <div className="border-b border-[#FFBD59]/40 bg-black/50 py-4 text-center">
          <h2 className="text-xl font-bold tracking-wide text-[#FFBD59]">
            Product Demo
          </h2>
          <p className="mt-1 text-sm text-gray-300">
            Watch the official AeroComp product demonstration.
          </p>
        </div>

        {/* VIDEO WRAPPER */}
        <div className="relative h-[60vh] w-full bg-black">
          {/* YouTube */}
          {source === 'youtube' && (
            <iframe
              className="absolute inset-0 h-full w-full rounded-b-xl"
              src={`${videoUrl}?autoplay=1&controls=1&modestbranding=1`}
              allow="autoplay; encrypted-media"
            />
          )}

          {/* MP4 */}
          {source === 'upload' && (
            <video
              ref={videoRef}
              className="absolute inset-0 h-full w-full rounded-b-xl object-cover"
              src={videoUrl ?? ''}
              controls
              autoPlay
            />
          )}
        </div>

        {/* FOOTER BUTTONS (Optional, Hide if not needed) */}
        <div className="flex items-center justify-end gap-4 border-t border-[#FFBD59]/40 bg-black/40 px-5 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-500 px-4 py-2 text-sm text-gray-300 transition hover:bg-gray-700/40"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
